import { Dispatch, SetStateAction, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ContentLayout, PageLayout } from 'layout';
import { MenuTitles, SettingsMenu } from 'constants/menu';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  GetRealestateCode,
  UpdateRealestateCode,
  UpdateDomainCode,
  GetDomainCode,
  GetCampaignFlowId,
  UpdateCampaignFlowId,
} from 'services';
import { Realestate } from 'types/realestate';
import { AgxToastState } from 'types/commonTypes';
import {
  AgxRow,
  AgxColumn,
  AgxHeader,
  AgxButton,
  AgxLabel,
  AgxTextInput,
  AgxDivider,
  AgxToast,
  Images,
  useWindowSize,
  Breakpoints,
} from '@urbanx/agx-ui-components';
import { Domain } from 'types/domain';
import './integrations.scss';

import { FormPrompt } from 'components/FormPrompt';
import { CampaignFlow } from 'types/campaignFlow';
import { AuthContext } from '@urbanx/agx.auth.client';
import { useSelectedAgency } from 'hooks';
import {
  useGetLabGroupAgencyCode,
  useInvalidateUseGetLabGroupAgencyCode,
  useUpdateLabGroupAgencyCode,
} from '@urbanx/agx.agencies.client';

interface AgencyCodeInfo {
  newCode: string | null;
  oldCode: string | null;
  isLoading: boolean;
  saved: boolean;
}

const IntegrationsPage = () => {
  const { agencyId } = useParams();
  const queryClient = useQueryClient();
  const { userAccount, getAuthToken } = useContext(AuthContext);
  const windowSize = useWindowSize();
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [tertiaryActiveMenu, setTertiaryActiveMenu] = useState<MenuTitles | undefined>(
    MenuTitles.INTEGRATIONS
  );
  const { agency: selectedAgency } = useSelectedAgency();

  enum AgencyCodes {
    Rea = 'REA Agency Code',
    Domain = 'Domain Agency Code',
    CampaignFlow = 'Campaign Flow ID',
    LabGroup = 'LAB Group Agency Code',
  }

  const regexMap = {
    [AgencyCodes.Rea]: /^[A-Z]{6}$/,
    [AgencyCodes.Domain]: /^\d{5}$/,
    [AgencyCodes.CampaignFlow]: /^\d{3,8}$/,
    [AgencyCodes.LabGroup]: /^[A-Z0-9]{0,20}$/,
  };

  const [realestateInfo, setRealestateInfo] = useState<AgencyCodeInfo>({
    newCode: null,
    oldCode: '',
    isLoading: false,
    saved: true,
  });
  const [domainInfo, setDomainInfo] = useState<AgencyCodeInfo>({
    newCode: null,
    oldCode: '',
    isLoading: false,
    saved: true,
  });
  const [campaignFlowInfo, setCampaignFlowInfo] = useState<AgencyCodeInfo>({
    newCode: null,
    oldCode: '',
    isLoading: false,
    saved: true,
  });
  const [labGroupInfo, setLabGroupInfo] = useState<AgencyCodeInfo>({
    newCode: null,
    oldCode: '',
    isLoading: false,
    saved: true,
  });
  const [toastState, updateToastState] = useState<AgxToastState>({
    color: 'success',
    message: '',
    open: false,
  });

  const realestateKey = [`realestate-code-${agencyId}`, agencyId];

  const { data: realestate, isLoading: realestateIsLoading } = useQuery<Realestate | undefined>({
    queryKey: realestateKey,
    queryFn: (queryInfo) => GetRealestateCode(queryInfo, getAuthToken),
  });

  const domainKey = [`domain-code-${agencyId}`, agencyId];

  const { data: domain, isLoading: domainIsLoading } = useQuery<Domain | undefined>({
    queryKey: domainKey,
    queryFn: (queryInfo) => GetDomainCode(queryInfo, getAuthToken),
  });

  const campaignFlowKey = [`campain-flow-id-${agencyId}`, agencyId];

  const { data: campaignFlow, isLoading: campaignFlowIsLoading } = useQuery<
    CampaignFlow | undefined
  >({
    queryKey: campaignFlowKey,
    queryFn: (queryInfo) => GetCampaignFlowId(queryInfo, getAuthToken),
  });

  const { data: labGroup, isPending: labGroupIsLoading } = useGetLabGroupAgencyCode(
    agencyId || '',
    !!agencyId
  );
  const invalidateLabGroupAgencyCode = useInvalidateUseGetLabGroupAgencyCode();

  // Mutations Section
  const { mutate: updateRealestateCode } = useMutation({
    mutationFn: UpdateRealestateCode,
    onSuccess: () => {
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: realestateKey,
        }),
      ]);
      updateToastState({
        color: 'success',
        message: 'REA agency code updated successfully',
        open: true,
      });
      setRealestateInfo({ ...realestateInfo, saved: true });
    },
    onError: () => {
      updateToastState({
        color: 'error',
        message: 'Error updating the REA agency code',
        open: true,
      });
    },
  });

  const { mutate: updateDomainCode } = useMutation({
    mutationFn: UpdateDomainCode,
    onSuccess: () => {
      setIsFormDirty(false);
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: domainKey,
        }),
      ]);
      updateToastState({
        color: 'success',
        message: 'Agency Domain code updated successfully',
        open: true,
      });
      setDomainInfo({ ...domainInfo, saved: true });
    },
    onError: () => {
      updateToastState({
        color: 'error',
        message:
          'Error updating the Domain agency code. Please ensure the agency code is less than 2,147,483,647',
        open: true,
      });
    },
  });

  const { mutate: updateCampaignFlowId } = useMutation({
    mutationFn: UpdateCampaignFlowId,
    onSuccess: () => {
      setIsFormDirty(false);
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: campaignFlowKey,
        }),
      ]);
      updateToastState({
        color: 'success',
        message: 'Campaign Flow Agency ID updated successfully',
        open: true,
      });
      setCampaignFlowInfo({ ...campaignFlowInfo, saved: true });
    },
    onError: () => {
      updateToastState({
        color: 'error',
        message: 'Error updating Campaign Flow Agency ID',
        open: true,
      });
    },
  });

  const { updateLabGroupAgencyCode } = useUpdateLabGroupAgencyCode({
    onSuccess: async () => {
      setIsFormDirty(false);
      await invalidateLabGroupAgencyCode(agencyId || '');
      updateToastState({
        color: 'success',
        message: 'LAB Group Agency Code updated successfully',
        open: true,
      });
      setLabGroupInfo({ ...labGroupInfo, saved: true });
    },
    onError: () => {
      updateToastState({
        color: 'error',
        message: 'Error updating LAB Group Agency Code',
        open: true,
      });
    },
  });

  // Saving Section
  const saveRealestateCodeChanges = () => {
    updateRealestateCode({
      AgencyId: agencyId || '',
      Code: realestateInfo.newCode,
      getAuthToken,
    });
  };

  const saveDomainCodeChanges = () => {
    updateDomainCode({
      AgencyId: agencyId || '',
      Code: parseInt(domainInfo.newCode || ''),
      getAuthToken,
    });
  };

  const saveCampaignFlowIdChanges = () => {
    updateCampaignFlowId({
      AgencyId: agencyId || '',
      Code: parseInt(campaignFlowInfo.newCode || ''),
      getAuthToken,
    });
  };

  const saveLabGroupAgencyCodeChanges = () => {
    updateLabGroupAgencyCode({
      agencyId: agencyId || '',
      code: labGroupInfo.newCode || undefined,
    });
  };

  // UseEffect Section
  useEffect(() => {
    setIsFormDirty(false);
    setRealestateInfo({
      ...realestateInfo,
      newCode: realestate?.code || null,
      oldCode: realestate?.code || '',
    });
  }, [realestate]);
  useEffect(() => {
    setIsFormDirty(false);
    setDomainInfo({
      ...domainInfo,
      newCode: domain?.code || null,
      oldCode: domain?.code || '',
    });
  }, [domain]);
  useEffect(() => {
    setIsFormDirty(false);
    setCampaignFlowInfo({
      ...campaignFlowInfo,
      newCode: campaignFlow?.code ? campaignFlow.code.toString() : null,
      oldCode: campaignFlow?.code ? campaignFlow.code.toString() : '',
    });
  }, [campaignFlow]);
  useEffect(() => {
    setIsFormDirty(false);
    setLabGroupInfo({
      ...labGroupInfo,
      newCode: labGroup?.code || '',
      oldCode: labGroup?.code || '',
    });
  }, [labGroup]);

  // Element Rendering Section
  const renderAgencyCodeElements = (
    title: AgencyCodes,
    codeInfo: AgencyCodeInfo,
    numberOnly: boolean,
    isUpperCase: boolean,
    alphanumericOnly: boolean,
    maxLength: number | undefined,
    stateFunction: Dispatch<SetStateAction<AgencyCodeInfo>>,
    updateFunction: () => void
  ) => {
    let textInput = (
      <AgxTextInput
        id={`input-${title}`}
        noOptionalLabel
        readonly={true}
        defaultValue="Loading..."
      />
    );

    if (!codeInfo.isLoading && codeInfo.oldCode !== null) {
      textInput = (
        <AgxTextInput
          id={`input-${title}`}
          key={title}
          noOptionalLabel
          noDecimalPoints
          numberOnly={numberOnly}
          isUpperCase={isUpperCase}
          alphanumericOnly={alphanumericOnly}
          maxLength={maxLength}
          defaultValue={codeInfo.oldCode}
          onInputValueChange={(info: { value: any }) => {
            if (info.value && codeInfo.oldCode !== info.value) {
              setIsFormDirty(true);
            } else {
              setIsFormDirty(false);
            }
            stateFunction({ ...codeInfo, newCode: info.value, saved: false });
          }}
        />
      );
    }

    let updaterElement = null;

    if (Object.values(AgencyCodes).includes(title as AgencyCodes)) {
      updaterElement = (
        <AgxButton
          text="Save Changes"
          medium
          primary
          disabled={
            codeInfo.newCode === codeInfo.oldCode ||
            (codeInfo.newCode !== '' &&
              !regexMap[title as AgencyCodes]?.test(codeInfo.newCode || ''))
          }
          onClick={() => updateFunction()}
        />
      );
    } else if (codeInfo.saved) {
      updaterElement = (
        <AgxRow centered mediumGap>
          <Images.Checkmark />
          <AgxLabel>Changes Saved</AgxLabel>
        </AgxRow>
      );
    }

    return (
      <AgxColumn extraLargeGap extraClasses="companyDetailsPanel container50Percent">
        <AgxRow spaceBetween extraClasses={'agency-codes-individual-headers'}>
          <AgxHeader size={5}>{title}</AgxHeader>
          {updaterElement}
        </AgxRow>
        <AgxDivider />
        <AgxRow veryLargeGap>{textInput}</AgxRow>
      </AgxColumn>
    );
  };

  return (
    <PageLayout
      agentName={userAccount?.firstName || ''}
      primaryPageTitle={selectedAgency?.name ?? ''}
      currentPageTitle={MenuTitles.SETTINGS}
    >
      <ContentLayout
        hasSecondaryMenu={true}
        secondaryActiveMenu={MenuTitles.SETTINGS}
        tertiaryMenu={SettingsMenu}
        tertiaryActiveMenu={tertiaryActiveMenu}
      >
        <FormPrompt hasUnsavedChanges={isFormDirty} />
        <AgxToast selector="#agxToast" toastState={toastState} />
        <AgxColumn veryLargeGap>
          <AgxRow
            spaceBetween
            centered
            extraClasses={`borderBottomContainer ${windowSize === Breakpoints.Desktop ? 'pageTitleContainerWithTertiaryMenu' : ''}`}
          >
            {windowSize === Breakpoints.Desktop && <AgxLabel large>{MenuTitles.SETTINGS}</AgxLabel>}
            {windowSize !== Breakpoints.Desktop && (
              <AgxButton
                id="btn-back"
                leftIcon={<Images.ArrowBackOutline />}
                naked
                large
                text={MenuTitles.INTEGRATIONS}
                onClick={() => setTertiaryActiveMenu(undefined)}
              />
            )}
          </AgxRow>
          <AgxColumn extraLargeGap>
            {renderAgencyCodeElements(
              AgencyCodes.Rea,
              { ...realestateInfo, isLoading: realestateIsLoading },
              false,
              false,
              false,
              undefined,
              setRealestateInfo,
              saveRealestateCodeChanges
            )}
            {renderAgencyCodeElements(
              AgencyCodes.Domain,
              { ...domainInfo, isLoading: domainIsLoading },
              true,
              false,
              false,
              undefined,
              setDomainInfo,
              saveDomainCodeChanges
            )}
            {renderAgencyCodeElements(
              AgencyCodes.CampaignFlow,
              { ...campaignFlowInfo, isLoading: campaignFlowIsLoading },
              true,
              false,
              false,
              undefined,
              setCampaignFlowInfo,
              saveCampaignFlowIdChanges
            )}
            {renderAgencyCodeElements(
              AgencyCodes.LabGroup,
              { ...labGroupInfo, isLoading: labGroupIsLoading },
              false,
              true,
              true,
              20,
              setLabGroupInfo,
              saveLabGroupAgencyCodeChanges
            )}
          </AgxColumn>
        </AgxColumn>
      </ContentLayout>
    </PageLayout>
  );
};

export default IntegrationsPage;
